import { API } from "@swivl-mono/api-helper";
import { DateHelper } from "@swivl-mono/helpers";
import {
  FacilityInfoDef,
  FacilityInfoKey,
  LocationVarKey,
  ScraperPreviewResult,
  UnfinishedScrape,
  featureOnValue,
  featureSwitches,
  getScrapableFacilityInfoDefs
} from "@swivl-mono/types";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  ImageUploader,
  ImageUploaderType,
  Input,
  InsetTabsComponent,
  Select,
  Spinner,
  Tooltip
} from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FiArrowRight, FiInfo } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { NavigateFunction } from "react-router-dom";

import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import ModalModel from "../../../Models/Modal/Modal.Model";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import { AddLocationWizard } from "./AddLocationWizard/AddLocationWizard";

interface Props {
  navigate: NavigateFunction;
  // Deep-link resume: open straight into the wizard for an existing location (from the onboarding ticket).
  resumeLocationId?: number;
  resumeSourceId?: number | null;
}

type TabId = "scrape" | "manual";

const ALL_SCRAPABLE = getScrapableFacilityInfoDefs();

// Every scrapable field except Name - Name drives the Location Name input directly instead of
// appearing as its own row, so there's no redundant "Facility Name" field alongside it.
const DISPLAY_FIELDS = ALL_SCRAPABLE.filter((def) => def.key !== FacilityInfoKey.Name);

const ALL_FEATURES = featureSwitches();

const HTTPS_URL = /^https:\/\/.+/i;

/** v3-only "Add Location" (kept separate from AddLocationsOrGroup.Modal.tsx) with Website Scrape and Manual tabs sharing Group state, where Manual stays hidden once a scrape is attempted except as the recovery path on a fetch error. */
export function AddLocationV3Modal(props: Props) {
  const { navigate, resumeLocationId, resumeSourceId } = props;
  const { groups } = LocationsModel.useState((s) => s);
  const [activeTab, setActiveTab] = useState<TabId>("scrape");
  const [scrapeAttempted, setScrapeAttempted] = useState(false);
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState<number | null>(null);
  const [url, setUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [preview, setPreview] = useState<ScraperPreviewResult | null>(null);
  const [fieldValues, setFieldValues] = useState<Partial<Record<FacilityInfoKey, string>>>({});
  const [featuresIncluded, setFeaturesIncluded] = useState<Record<string, boolean>>({});
  // Set of keys the scraper originally found — used for sorting only,
  // so manually toggling a checkbox doesn't re-order the list.
  const [scrapedFeatureKeys, setScrapedFeatureKeys] = useState<Set<string>>(new Set());
  const [featureCitations, setFeatureCitations] = useState<Record<string, string>>({});
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Background policy-content scan: polled after a successful scrape.
  const [scanStatus, setScanStatus] = useState<"idle" | "scanning" | "ready" | "unavailable">("idle");
  const [scanProgress, setScanProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  // Guided setup after create/resume (FMS -> hours -> policies -> Google).
  const [wizardActive, setWizardActive] = useState(false);
  const [createdLocationId, setCreatedLocationId] = useState<number | null>(null);
  // Source row backing the review — set on create-and-review or when resuming an unfinished scrape.
  const [reviewSourceId, setReviewSourceId] = useState<number | null>(null);
  // Scrapes started earlier whose location exists but review was never signed off.
  const [unfinished, setUnfinished] = useState<UnfinishedScrape[]>([]);

  useEffect(() => {
    if (groups && groups.length > 0 && groupId === null) {
      setGroupId(groups[0].id);
    }
  }, [groups]);

  // Deep-link resume (from the ticket): jump straight into the wizard for an existing location.
  useEffect(() => {
    if (resumeLocationId == null) return;
    const enter = async (): Promise<void> => {
      let sourceId = resumeSourceId ?? null;
      // Derive the scrape source from the location when the caller didn't supply one.
      if (sourceId == null) {
        try {
          const unfinished = await API.Scraper.listUnfinished(Member.orgId);
          sourceId = unfinished.find((scrape) => scrape.locationId === resumeLocationId)?.id ?? null;
        } catch {
          // No scrape source (manual add) — the wizard skips the policies step.
        }
      }
      setCreatedLocationId(resumeLocationId);
      setReviewSourceId(sourceId);
      setWizardActive(true);
    };
    void enter();
  }, [resumeLocationId, resumeSourceId]);

  // Load resumable scrapes once; failure (e.g. flag off) just leaves the list empty.
  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        setUnfinished(await API.Scraper.listUnfinished(Member.orgId));
      } catch {
        setUnfinished([]);
      }
    };
    void load();
  }, []);

  // Poll the scrape row until the background policy scan finishes, driving the progress bar.
  useEffect(() => {
    const scrapeId = preview?.id;
    if (!scrapeId || preview?.fetchError) {
      setScanStatus("idle");
      return;
    }
    setScanStatus("scanning");
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (): Promise<void> => {
      try {
        const row = await API.DocumentIngest.getUpload(Member.orgId, scrapeId);
        if (cancelled) return;
        if (row) {
          setScanProgress({ completed: row.scanCompletedCalls ?? 0, total: row.scanTotalCalls ?? 0 });
          if (row.status === "extracted" || row.status === "failed") {
            setScanStatus("ready");
            return;
          }
        }
        timer = setTimeout(() => void poll(), 2000);
      } catch {
        // Policy review unavailable for this org (flag off) — fall back to plain create.
        if (!cancelled) setScanStatus("unavailable");
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [preview?.id, preview?.fetchError]);

  const manualTabVisible = !scrapeAttempted || !!preview?.fetchError;

  // Fill the form from a preview (fresh scrape or a rebuilt one); fallbackUrl backstops a missing website.
  const populateFromPreview = (result: ScraperPreviewResult, fallbackUrl?: string): void => {
    const found = new Map([...result.autoApply, ...result.needsReview].map((field) => [field.facilityInfoKey, field.value]));
    if (fallbackUrl && !found.get(FacilityInfoKey.WebsiteURL)) found.set(FacilityInfoKey.WebsiteURL, fallbackUrl);
    setFieldValues(Object.fromEntries(DISPLAY_FIELDS.map((def) => [def.key, found.get(def.key) ?? ""])));
    setScrapedFeatureKeys(new Set(result.features.map((f) => f.key)));
    setFeaturesIncluded(Object.fromEntries(result.features.map((f) => [f.key, true])));
    setFeatureCitations(Object.fromEntries(result.features.filter((f) => f.citation).map((f) => [f.key, f.citation])));
    setPhotoUrl(result.photoUrl);
    const scrapedName = found.get(FacilityInfoKey.Name);
    if (scrapedName && !name.trim()) setName(scrapedName);
  };

  const handleScrape = async (): Promise<void> => {
    if (!HTTPS_URL.test(url.trim())) return;
    setScrapeAttempted(true);
    setScraping(true);
    setPhotoUrl(null);
    setError("");
    try {
      const result = await API.Scraper.previewScrape(url.trim(), Member.orgId);
      setPreview(result);
      if (!result.fetchError) populateFromPreview(result, url.trim());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setScraping(false);
    }
  };

  const fieldValue = (def: FacilityInfoDef): string => (def.key === FacilityInfoKey.Name ? name : (fieldValues[def.key] ?? ""));

  // Create the location, apply scraped fields/features + photo; returns the new id, no navigation.
  const createLocationWithPatches = async (): Promise<number | null> => {
    if (groupId === null) return null;
    const location = await LocationsModel.createLocation(name, groupId);
    const orgId = Member.orgId;
    if (orgId && location?.id) {
      const patches = [
        ...ALL_SCRAPABLE.filter((def) => fieldValue(def).trim() !== "").map((def) => ({
          key: def.cortexVarKey,
          value: fieldValue(def).trim(),
          source: "scraper"
        })),
        ...ALL_FEATURES.filter((f) => featuresIncluded[f.key]).map((f) => ({
          key: f.cortexVarKey,
          value: featureOnValue(true),
          source: "scraper"
        }))
      ];
      const [saveResult] = await Promise.all([
        patches.length > 0 ? API.Cortex.saveRaw({ orgId, locationId: location.id, patches }) : Promise.resolve({ errors: [] }),
        photoUrl ? LocationVarsModel.createVariable(LocationVarKey.CortexFacilityImageURL, photoUrl, undefined, location.id) : Promise.resolve()
      ]);
      if (saveResult.errors.length > 0) {
        AlertsModel.errorToast(
          `Location created, but ${saveResult.errors.length} field(s) couldn't be saved - fix them in Property Manager directly.`
        );
      }
      await LocationVarsModel.loadAllVariables(true);
    }
    return location?.id ?? null;
  };

  const finishToLocation = (id: number): void => {
    ModalModel.hideModal();
    navigate(`/properties-v3/group/${groupId}/location/${id}/page/details-v3/facility-info`);
  };

  // Reopen an unfinished scrape: into the wizard (at the first incomplete step) if a location
  // exists, else back to the create form.
  const handleResume = async (scrape: UnfinishedScrape): Promise<void> => {
    if (scrape.locationId != null) {
      setCreatedLocationId(scrape.locationId);
      setReviewSourceId(scrape.id);
      setWizardActive(true);
      return;
    }
    const result = await API.Scraper.rebuildPreview(Member.orgId, scrape.id);
    setScrapeAttempted(true);
    // Pre-fill the URL so one Scrape click re-fetches the page (and its photo, not persisted).
    setUrl(scrape.source);
    setPreview(result);
    populateFromPreview(result, scrape.source);
  };

  // Dismiss an unfinished scrape and drop it from the list.
  const handleDismiss = async (id: number): Promise<void> => {
    setUnfinished((prev) => prev.filter((scrape) => scrape.id !== id));
    try {
      await API.Scraper.discardUnfinished(Member.orgId, id);
    } catch {
      // Best-effort; the row simply reappears on next open if the delete failed.
    }
  };

  // Create the location, then enter the guided setup wizard instead of navigating away.
  const handleCreateAndContinue = async (): Promise<void> => {
    if (groupId === null) return;
    setLoading(true);
    try {
      const id = await createLocationWithPatches();
      if (id != null) {
        // Link the scrape row to the new location now, so it's resumable and its % is measurable.
        if (preview?.id != null) await API.DocumentIngest.applyFindings(Member.orgId, preview.id, { mapKeys: [], locationId: id, finish: false });
        setCreatedLocationId(id);
        setReviewSourceId(preview?.id ?? null);
        setWizardActive(true);
      }
      setLoading(false);
    } catch (e) {
      setError((e as Error).message);
      setLoading(false);
    }
  };

  const tabItems = [
    { id: "scrape", label: "Website Scrape", badge: false },
    ...(manualTabVisible ? [{ id: "manual", label: "Manual", badge: false }] : [])
  ];
  // If the visible-Manual window closes (a later scrape attempt succeeds) while it was active, land back on Scrape.
  const effectiveTab: TabId = activeTab === "manual" && !manualTabVisible ? "scrape" : activeTab;

  const groupField = (
    <Select
      label="Group"
      options={groups?.map((group) => ({ label: group.name, value: String(group.id) }))}
      selectedValues={groupId ? [String(groupId)] : []}
      onChange={(values) => {
        const selectedValue = values[0];
        setGroupId(selectedValue ? parseInt(selectedValue) : null);
      }}
      hasSearchInDropdown
    />
  );

  const nameField = <Input label="Location Name" value={name} onChange={(e) => setName(e.target.value)} />;

  if (wizardActive && createdLocationId != null && groupId != null) {
    return (
      <AddLocationWizard
        locationId={createdLocationId}
        groupId={groupId}
        reviewSourceId={reviewSourceId}
        onClose={() => ModalModel.hideModal()}
        onDone={() => finishToLocation(createdLocationId)}
      />
    );
  }

  // Resumable scrapes shown before a fresh scrape starts; location-created ones resume into review,
  // the rest reopen the create form.
  const resumable = unfinished;

  return (
    <div className="flex max-h-[75vh] w-[640px] flex-col">
      {manualTabVisible && <InsetTabsComponent items={tabItems} selectedItem={effectiveTab} itemSelected={(id) => setActiveTab(id as TabId)} />}

      <div className="flex-1 overflow-y-auto px-1 pt-3">
        {effectiveTab === "manual" && (
          <>
            {nameField}
            <div className="mt-3">{groupField}</div>
          </>
        )}

        {effectiveTab === "scrape" && (
          <>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input label="Location Page" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" />
              </div>
              <Button
                color={ButtonColor.TEAL}
                variant={ButtonVariant.PRIMARY}
                loading={scraping}
                disabled={!HTTPS_URL.test(url.trim())}
                onClick={handleScrape}
              >
                Scrape
              </Button>
            </div>

            {scraping && (
              <div className="mt-3 flex items-center gap-2 text-grey-600">
                <Spinner /> Reading the page…
              </div>
            )}

            <div className="mt-3">{groupField}</div>

            {!preview && !scraping && resumable.length > 0 && (
              <div className="mt-4 flex flex-col gap-2 rounded-md border border-grey-200 bg-grey-50 p-3">
                <div className="body-xs font-semibold text-grey-600">Or pick up where you left off</div>
                {resumable.map((scrape) => (
                  <div key={scrape.id} className="flex items-center gap-3 rounded bg-white px-3 py-2">
                    <Tooltip content="Dismiss">
                      <button
                        type="button"
                        className="flex shrink-0 text-grey-400 hover:text-grey-700"
                        aria-label="Dismiss"
                        onClick={() => void handleDismiss(scrape.id)}
                      >
                        <IoClose className="size-4" />
                      </button>
                    </Tooltip>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="body-sm truncate font-medium text-grey-900">{scrape.name ?? scrape.source}</span>
                      <span className="body-xs truncate text-grey-500">
                        {scrape.address ? `${scrape.address} · ` : ""}
                        {scrape.scanning
                          ? "Still scanning policies…"
                          : scrape.percentComplete != null
                            ? `${scrape.percentComplete}% complete`
                            : "Not started"}
                        {` · edited ${DateHelper.timeAgoShort(scrape.createdAt)} ago`}
                      </span>
                    </span>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      color={ButtonColor.ORANGE}
                      size={ButtonSize.SMALL}
                      leftIcon={<FiArrowRight />}
                      onClick={() => void handleResume(scrape)}
                    >
                      Finish Onboarding
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {preview?.fetchError && (
              <button
                type="button"
                className="mt-2 flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700"
                onClick={() => setActiveTab("manual")}
              >
                Error scraping that page. <u>Enter details manually →</u>
              </button>
            )}

            {preview && !preview.fetchError && (
              <div className="mt-4 flex flex-col gap-4">
                {preview.duplicateOf && (
                  <div className="body-sm flex items-start gap-2.5 rounded-md border border-grey-200 bg-grey-50 px-3.5 py-3">
                    <FiInfo className="mt-0.5 size-4 shrink-0 text-grey-500" />
                    <span className="flex-1 text-grey-900">
                      Based on its {preview.duplicateOf.matchedOn}, this looks like <span className="font-semibold">{preview.duplicateOf.name}</span>,
                      which has already been added to Property Manager.{" "}
                      <a
                        href={`/properties-v3/group/${preview.duplicateOf.groupId}/location/${preview.duplicateOf.locationId}/page/details-v3/facility-info`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-orange-500 hover:text-orange-600 hover:underline"
                      >
                        Edit it instead <FiArrowRight className="size-4" />
                      </a>
                    </span>
                  </div>
                )}
                {preview.truncated && (
                  <div className="body-xs rounded bg-yellow-50 px-2 py-1 text-yellow-800">
                    This page was long — some content further down may not have been read. Double-check for anything missed.
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div className="shrink-0">
                    <ImageUploader
                      type={ImageUploaderType.LOCATION}
                      isEditable
                      imageURL={photoUrl ?? undefined}
                      text="Click to upload"
                      imageUploaded={(url) => setPhotoUrl(url)}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-4">
                    <div>{nameField}</div>
                    {(fieldValues[FacilityInfoKey.Address] ?? "").trim() !== "" && (
                      <Input
                        label="Address"
                        value={fieldValues[FacilityInfoKey.Address] ?? ""}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, [FacilityInfoKey.Address]: e.target.value }))}
                      />
                    )}
                  </div>
                </div>

                {DISPLAY_FIELDS.filter((def) => def.key !== FacilityInfoKey.Address && (fieldValues[def.key] ?? "").trim() !== "").map((def) => (
                  <Input
                    key={def.key}
                    label={def.label}
                    value={fieldValues[def.key] ?? ""}
                    onChange={(e) => setFieldValues((prev) => ({ ...prev, [def.key]: e.target.value }))}
                  />
                ))}

                <FieldGroup title="Features">
                  <div className="grid grid-cols-2 gap-2">
                    {[...ALL_FEATURES]
                      .sort((a, b) => {
                        const aFound = scrapedFeatureKeys.has(a.key);
                        const bFound = scrapedFeatureKeys.has(b.key);
                        if (aFound !== bFound) return aFound ? -1 : 1;
                        return a.label.localeCompare(b.label);
                      })
                      .map((feature) => (
                        <div key={feature.key} className="flex flex-col gap-0.5">
                          <Checkbox
                            name={feature.key}
                            label={feature.label}
                            checked={featuresIncluded[feature.key] ?? false}
                            onCheckedChange={(checked) => setFeaturesIncluded((prev) => ({ ...prev, [feature.key]: checked === true }))}
                          />
                          {featureCitations[feature.key] && <p className="body-xs pl-6 text-grey-500 italic">"{featureCitations[feature.key]}"</p>}
                        </div>
                      ))}
                  </div>
                </FieldGroup>
              </div>
            )}
          </>
        )}

        {error && <div className="mt-3 text-red-600">{error}</div>}
      </div>

      <div className="mt-4 flex flex-row items-center justify-between border-t border-grey-200 pt-3">
        <div className="min-w-0 flex-1 pr-4">
          {scanStatus === "scanning" && (
            <div className="w-64">
              <div className="body-xs mb-1 text-grey-600">
                Scraping for policy info...{scanProgress.total > 0 ? ` ${scanProgress.completed} of ${scanProgress.total}` : ""}
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-grey-200">
                <div
                  className="h-full rounded-full bg-orange-500 transition-all"
                  style={{ width: `${scanProgress.total > 0 ? Math.round((scanProgress.completed / scanProgress.total) * 100) : 8}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-row items-center">
          <Button variant={ButtonVariant.GHOST} loading={loading} onClick={() => ModalModel.hideModal()} className="mr-2">
            Cancel
          </Button>
          <Button
            color={ButtonColor.ORANGE}
            loading={loading}
            disabled={!name.trim() || groupId === null}
            onClick={() => void handleCreateAndContinue()}
          >
            Create Location &amp; Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <div className="body-xs mb-1 font-medium text-grey-600">{title}</div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}
